import React from 'react'
import Icon from 'components/elements/Icon'
import { cloneDeep } from 'lodash'

import {
  CollectionColumn,
  CollectionTag,
  CollectionV2Card,
  SaturatedCollectionColumnOptions,
} from 'redux/collectionV2/types'
import { CardActions, LocalCollectionV2CardData } from 'components/collectionV2/views/CardWrapper'

import { COND_ID_COND_ABR, COND_ID_COND_NAME, LANG_ID_TO_CODE, LANG_ID_TO_NAME } from 'types/collection'

import LabelDisplay from 'components/collectionV2/misc/LabelDisplay'
import ArchidektDropdown from 'components/elements/ArchidektDropdown'
import Dropdown from 'components/formElements/Dropdown'
import AsyncEditionDropdown from 'components/cardSearchPanel/searchFormElements/asyncEditionDropdown'
import Input from 'components/formElements/Input'
import { LabelOptionsDropdown } from 'components/collectionV2/cardDetails/Form/LabelOptions'
import { cardManaCostToElement } from 'components/misc/customIcons/ManaSymbol/helpers'
import { setCodeToElement } from 'components/misc/customIcons/SetSymbols'

import { getPriceStringAndUrl } from 'utils/Price'

import styles from './textCollectionCard.module.scss'

export const AUTO_COLUMNS: CollectionColumn[] = [
  'quantity',
  'name',
  'rarity',
  'set',
  'modifier',
  'language',
  'condition',
  'priceOne',
  'actions',
]

export const SORTABLE_HEADERS: CollectionColumn[] = [
  'name',
  'quantity',

  'rarity',
  'set',
  'editionDate',

  'createdAt',
  'updatedAt',

  'power',
  'toughness',
  'loyalty',

  'priceOne',
  'priceTwo',

  'artist',
  'manaValue',
]

// Classes that will always be applied to the column
export const COLUMN_CLASSES: Partial<Record<CollectionColumn, string>> = {
  name: styles.cardName,
  quantity: styles.quantity,
  colorLabels: styles.colorLabelColumn,
  actions: styles.actions,
  set: styles.edition,
  rarity: styles.rarity,
  setCode: styles.setCode,
  modifier: styles.finishColumn,
  language: styles.langCondition,
  condition: styles.langCondition,
}

// Classes that will only be applied to the column if autoColumns is true
export const AUTO_COLUMN_CLASSES: Partial<Record<CollectionColumn, string>> = {
  ...COLUMN_CLASSES,

  language: styles.langConditionAuto,
  condition: styles.langConditionAuto,
  priceOne: styles.price,
  set: styles.editionAuto,
  rarity: styles.rarityAuto,
  modifier: styles.finishColumnAuto,
}

export const COLUMN_HEADERS: Record<CollectionColumn, React.ReactNode> = {
  quantity: 'Qty',
  name: 'Card name',
  set: 'Edition',
  createdAt: 'Date added',
  updatedAt: 'Date updated',
  colorLabels: 'Collection labels',
  modifier: 'Finish',
  language: 'Language',
  condition: 'Condition',
  priceOne: 'Price 1',
  priceTwo: 'Price 2',
  purchasePrice: 'Purchase price',
  artist: 'Artist',
  editionDate: 'Edition date',
  casingCost: 'Casting cost',
  manaValue: 'Mana value',
  rarity: 'Rarity',
  collectorNumber: 'Collector No.',
  types: 'Types',
  power: 'Power',
  toughness: 'Toughness',
  loyalty: 'Loyalty',
  uid: 'Scryfall ID',
  setCode: 'Code',
  actions: '',
}

export const generateSaturatedColumns = (
  collectionCard: CollectionV2Card,
  actions: CardActions,
  localCardData: LocalCollectionV2CardData,
  priceSourceOne: number,
  priceSourceTwo: number,
  tags: Record<string, CollectionTag>,
  ownsCollection: boolean,
  autoColumns?: boolean,
): Partial<Record<CollectionColumn, SaturatedCollectionColumnOptions>> => {
  const price = getPriceStringAndUrl(
    { ...collectionCard.card, modifier: collectionCard.modifier },
    priceSourceOne,
    collectionCard.modifier !== 'Normal',
  )
  const price2 = getPriceStringAndUrl(
    { ...collectionCard.card, modifier: collectionCard.modifier },
    priceSourceTwo,
    collectionCard.modifier !== 'Normal',
  )

  const columnData = {
    name: {
      content: <span className={styles.ellipsis}>{collectionCard.card.name}</span>,
      onClick: actions.cardClick,
    },

    set: (
      <AsyncEditionDropdown
        noIcon
        card={collectionCard.card}
        containerClassName={styles.setDropdownContainer}
        triggerClassName={styles.setDropdownTrigger}
        disabled={localCardData.anyMultiSelected || !ownsCollection}
        onCardSelected={card => actions.onCardUpdate({ ...collectionCard, modifier: card.modifier, card })}
      />
    ),

    quantity: (
      <Input
        type="number"
        highlightOnFocus
        disabled={!ownsCollection}
        value={localCardData.quantity}
        className={styles.quantityInput}
        onChange={value => actions.setLocalQuantity(Number(value) || 0)}
        onBlur={value =>
          collectionCard.quantity !== value && actions.onCardUpdate({ ...collectionCard, quantity: Number(value) || 0 })
        }
      />
    ),
    modifier: (
      <Dropdown
        noIcon
        value={collectionCard.modifier || 'Normal'}
        disabled={collectionCard.card.options.length < 2 || localCardData.anyMultiSelected || !ownsCollection}
        onChange={modifier => actions.onCardUpdate({ ...collectionCard, modifier })}
        options={collectionCard.card.options.map(o => ({ value: o, label: o }))}
      />
    ),
    language: (
      <Dropdown
        noIcon
        disabled={!ownsCollection}
        value={LANG_ID_TO_CODE[collectionCard.language]}
        onChange={language => actions.onCardUpdate({ ...collectionCard, language })}
        options={Object.keys(LANG_ID_TO_NAME).map(key => ({ value: key, label: LANG_ID_TO_NAME[key] }))}
      />
    ),
    condition: (
      <Dropdown
        noIcon
        disabled={!ownsCollection}
        value={COND_ID_COND_ABR[collectionCard.condition]}
        onChange={condition => actions.onCardUpdate({ ...collectionCard, condition })}
        options={Object.keys(COND_ID_COND_NAME).map(key => ({ value: key, label: COND_ID_COND_NAME[key] }))}
      />
    ),
    createdAt: new Date(collectionCard.createdAt).toLocaleDateString(),
    priceOne: (
      <a href={price.url} rel="noopener noreferrer" target="_blank" className={styles.price}>
        {price.price}
      </a>
    ),
    priceTwo: (
      <a href={price2.url} rel="noopener noreferrer" target="_blank" className={styles.price}>
        {price2.price}
      </a>
    ),
    purchasePrice: (
      <Input
        type="number"
        highlightOnFocus
        placeholder="None"
        disabled={!ownsCollection}
        initialValue={collectionCard.purchasePrice}
        recycleInitialValueOn={collectionCard.purchasePrice}
        className={styles.purchasePriceInput}
        onBlur={value =>
          collectionCard.quantity !== value &&
          actions.onCardUpdate({ ...collectionCard, purchasePrice: Number(value) || null })
        }
      />
    ),

    editionDate: collectionCard.card.setDate,
    updatedAt: collectionCard.modifiedAt ? new Date(collectionCard.modifiedAt).toLocaleDateString() : 'None',
    casingCost: cardManaCostToElement(collectionCard.card),
    manaValue: collectionCard.card.cmc,
    rarity: {
      content: setCodeToElement(collectionCard.card.setCode, collectionCard.card.rarity, styles.setSymbol),
      onClick: () => ownsCollection && actions.openCardModal('details', true),
    },
    collectorNumber: collectionCard.card.collectorNumber,
    power: collectionCard.card.power,
    toughness: collectionCard.card.toughness,
    loyalty: collectionCard.card.loyalty,
    types: collectionCard.card.types.join(' '),
    uid: collectionCard.card.uid,
    setCode: {
      content: collectionCard.card.setCode.toLocaleUpperCase(),
      onClick: () => ownsCollection && actions.openCardModal('details', true),
    },
    actions: localCardData.anyMultiSelected ? null : (
      <ArchidektDropdown
        menuClassName={styles.overflowActionsMenu}
        options={[
          { label: 'Card options', icon: 'cog', keybind: 'Click', onClick: () => actions.openCardModal('details') },
          { type: 'spacer' },
          { label: 'View decks', onClick: () => actions?.openCardModal('decks'), icon: 'arrow right' },
          { label: 'View other records', onClick: () => actions?.openCardModal('relatedCards'), icon: 'arrow right' },
          {
            label: 'Delete card',
            onClick: () => actions?.deleteCard(),
            keybind: 'R',
            icon: 'trash',
            hidden: !ownsCollection,
          },
          { type: 'spacer', hidden: !ownsCollection },
          {
            label: 'Multi-select',
            icon: 'hand pointer outline',
            keybind: 'Ctrl + Click',
            onClick: () => actions.multiSelect(),
          },
          { label: 'Copy card name', icon: 'copy', onClick: actions.copyCardName },
        ]}
      />
    ),

    colorLabels: (
      <LabelOptionsDropdown
        disabled={!ownsCollection || localCardData.anyMultiSelected}
        triggerClassName={styles.colorLabelTrigger}
        cardTags={collectionCard.tags}
        onTagsUpdated={tags => actions.onCardUpdate({ ...collectionCard, tags })}>
        {collectionCard.tags
          .filter(tagId => !!tags[tagId]) // Filter out any tags that may have been deleted
          .map((id, key) => (
            <LabelDisplay key={key} name={tags[id]?.name} color={tags[id]?.color} className={styles.colorLabel} />
          ))}

        {!collectionCard.tags.filter(tagId => !!tags[tagId]).length && (
          <span className={styles.noTags}>No labels selected...</span>
        )}
      </LabelOptionsDropdown>
    ),
    artist: collectionCard.card.artist,
  }

  const response: Partial<Record<CollectionColumn, SaturatedCollectionColumnOptions>> = {}

  // ts is dumb here and you can't type this inline
  let key: CollectionColumn

  for (key in columnData) {
    const column = columnData[key]

    if (column === null) {
      response[key] = null

      continue
    }

    // @ts-ignore
    const content = column?.content || column

    // @ts-ignore
    const onClick = column?.onClick || undefined

    // @ts-ignore
    const className = (autoColumns ? AUTO_COLUMN_CLASSES[key] : COLUMN_CLASSES[key]) || undefined

    // @ts-ignore
    response[key] = { content, className, onClick }
  }

  return response
}

export const generateSaturatedHeaders = (
  handleChangeSort: (column: CollectionColumn, priceSource?: string) => void,
  currentSort: string,
  currentDirection: 'ascending' | 'descending',
  priceSourceOne: string,
  priceSourceTwo: string,
  autoColumns?: boolean,
): Partial<Record<CollectionColumn, SaturatedCollectionColumnOptions>> => {
  const response: Partial<Record<CollectionColumn, SaturatedCollectionColumnOptions>> = {}

  // ts is dumb here and you can't type this inline
  let key: CollectionColumn

  for (key in COLUMN_HEADERS) {
    // Neccecary to clone the key here because the arument is being passed by reference to key, whichc is always the last item in the loop
    const columnKey = cloneDeep(key)

    // Appending extra data to the column
    let onClick = SORTABLE_HEADERS.includes(key) ? () => handleChangeSort(columnKey) : undefined

    if (key === 'priceOne') onClick = () => handleChangeSort('priceOne', priceSourceOne)
    if (key === 'priceTwo') onClick = () => handleChangeSort('priceTwo', priceSourceTwo)

    const className = (autoColumns ? AUTO_COLUMN_CLASSES[key] : COLUMN_CLASSES[key]) || undefined
    const content = (
      <>
        {COLUMN_HEADERS[key]}
        {key === currentSort && (
          <Icon name={currentDirection === 'ascending' ? 'sort amount up' : 'sort amount down'} />
        )}
      </>
    )

    // @ts-ignore
    response[key] = { content, className, onClick }
  }

  return response
}
